import { resolveMetricDeltaArrowDirection } from '@/features/shared/utils/metricDeltaArrow';
import { resolveTableMetricAccentColor } from '@/features/shared/utils/tableMetricPresentation';
import { getStatusCopy } from '@/shared/copy/status';
import { resolveSemanticColor, type SemanticColorKey } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Box } from '@/ui/primitives';
import { TrendingDownIcon, TrendingUpIcon } from '@/ui/primitives/icons';
import { ChartPanelTitle } from './ChartPanelTitle';
import type { Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type MiniMetricHeaderTone =
  | 'neutral'
  | 'green'
  | 'red'
  | 'orange'
  | 'brown'
  | 'blue'
  | 'purple'
  | 'teal';

export type MiniMetricChartSummary = {
  valueLabel: string;
  valueTone: MiniMetricHeaderTone;
  deltaLabel: string | null;
  deltaValue: number | null;
  deltaTone: 'positive' | 'negative' | 'neutral';
  deltaColor?: SemanticColorKey;
  isMissing: boolean;
};

type MiniMetricChartHeaderProps = {
  title: string;
  summary: MiniMetricChartSummary | null;
  valueFontSize?: string | number;
};

const deltaTextSx = {
  color: 'text.secondary',
  fontSize: 'subtitle2.fontSize',
  lineHeight: '18px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('md'),
  whiteSpace: 'nowrap',
} as const;

export const MiniMetricChartHeader = ({
  title,
  summary,
  valueFontSize,
}: MiniMetricChartHeaderProps): ReactElement => {
  const deltaLabel = summary?.deltaLabel ?? null;
  const deltaValue = summary?.deltaValue ?? null;
  const deltaTone = summary?.deltaTone ?? 'neutral';
  const resolvedDeltaColor = summary?.deltaColor;
  const showDeltaArrow =
    deltaLabel !== null &&
    deltaLabel !== getStatusCopy('noChange') &&
    deltaValue !== null &&
    deltaValue !== 0;
  const deltaArrowDirection = resolveMetricDeltaArrowDirection(deltaValue);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: s('sm'),
      }}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <ChartPanelTitle title={title} component="span" showInfoIcon={false} />
      </Box>
      {summary ? (
        <Box
          sx={{
            minWidth: 0,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: s('md'),
            rowGap: s('xxs'),
            flex: '1 1 auto',
            flexWrap: 'wrap',
            textAlign: 'right',
          }}
        >
          <Box
            component="span"
            sx={{
              color: summary.isMissing
                ? 'text.secondary'
                : (theme) => resolveTableMetricAccentColor(theme, summary.valueTone),
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: summary.isMissing ? '15px' : (valueFontSize ?? '13px'),
              lineHeight:
                valueFontSize !== undefined
                  ? '1.2'
                  : (theme: Theme) => theme.typography.subtitle1.lineHeight,
              fontWeight: (theme) => theme.typography.fontWeightRegular,
            }}
          >
            {summary.valueLabel}
          </Box>
          {deltaLabel ? (
            <Box
              component="span"
              sx={[
                deltaTextSx,
                resolvedDeltaColor
                  ? {
                      color: (theme) => resolveSemanticColor(theme, resolvedDeltaColor),
                    }
                  : {},
              ]}
            >
              {deltaLabel}
              {showDeltaArrow ? (
                <Box
                  component="span"
                  sx={{
                    color: resolvedDeltaColor
                      ? (theme) => resolveSemanticColor(theme, resolvedDeltaColor)
                      : deltaTone === 'positive'
                        ? 'success.main'
                        : deltaTone === 'negative'
                          ? 'error.main'
                          : 'text.secondary',
                    fontSize: 'body.fontSize',
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {deltaArrowDirection === 'down' ? (
                    <TrendingDownIcon sx={{ fontSize: 'inherit' }} />
                  ) : (
                    <TrendingUpIcon sx={{ fontSize: 'inherit' }} />
                  )}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
